"use client"

/**
 * Demo state submenu — grouped by surface so reviewers can combine blueprint,
 * Add question, and Assessment Questions tab cases independently.
 */

import {
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu"
import {
  DEMO_ADD_QUESTION_OPTIONS,
  DEMO_ASSESSMENT_PAPER_OPTIONS,
  DEMO_BLUEPRINT_OPTIONS,
  DEMO_IMPORT_OPTIONS,
  useDemoState,
  type DemoAddQuestionId,
  type DemoAssessmentPaperId,
  type DemoBlueprintId,
} from "@/contexts/demo-state-context"

function DemoOptionItem({
  label,
  hint,
}: {
  label: string
  hint: string
}) {
  return (
    <span className="flex flex-col gap-0.5">
      <span>{label}</span>
      <span className="text-xs text-muted-foreground">{hint}</span>
    </span>
  )
}

export function DemoStateMenu() {
  const {
    demoState,
    setDemoBlueprint,
    setDemoAddQuestion,
    setDemoAssessmentPaper,
  } = useDemoState()

  const bankChoice =
    demoState.addQuestion === "default" ||
    demoState.addQuestion === "empty-bank"
      ? demoState.addQuestion
      : ""

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <i className="fa-light fa-flask" aria-hidden="true" />
        Demo state
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-72">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Blueprint</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-72">
            <DropdownMenuRadioGroup
              value={demoState.blueprint}
              onValueChange={(value) =>
                setDemoBlueprint(value as DemoBlueprintId)
              }
            >
              {DEMO_BLUEPRINT_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option.id}
                  value={option.id}
                  className="items-start"
                >
                  <DemoOptionItem label={option.label} hint={option.hint} />
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Add question</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-72">
            <DropdownMenuRadioGroup
              value={bankChoice}
              onValueChange={(value) =>
                setDemoAddQuestion(value as DemoAddQuestionId)
              }
            >
              {DEMO_ADD_QUESTION_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option.id}
                  value={option.id}
                  className="items-start"
                >
                  <DemoOptionItem label={option.label} hint={option.hint} />
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="mx-1 rounded-sm">
                Import
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent className="w-72">
                <DropdownMenuRadioGroup
                  value={
                    DEMO_IMPORT_OPTIONS.some(
                      (option) => option.id === demoState.addQuestion,
                    )
                      ? demoState.addQuestion
                      : ""
                  }
                  onValueChange={(value) =>
                    setDemoAddQuestion(value as DemoAddQuestionId)
                  }
                >
                  {DEMO_IMPORT_OPTIONS.map((option) => (
                    <DropdownMenuRadioItem
                      key={option.id}
                      value={option.id}
                      className="items-start"
                    >
                      <DemoOptionItem label={option.label} hint={option.hint} />
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>Assessment Question tab</DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="w-72">
            <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
              Paper on the Questions tab
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={demoState.assessmentPaper}
              onValueChange={(value) =>
                setDemoAssessmentPaper(value as DemoAssessmentPaperId)
              }
            >
              {DEMO_ASSESSMENT_PAPER_OPTIONS.map((option) => (
                <DropdownMenuRadioItem
                  key={option.id}
                  value={option.id}
                  className="items-start"
                >
                  <DemoOptionItem label={option.label} hint={option.hint} />
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  )
}
